import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { map, catchError, finalize, timeout, switchMap, filter, take } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

const APP_XHR_TIMEOUT = 30000;

@Injectable()
export class AppInterceptor implements HttpInterceptor {
	private isRefreshing = false;
	private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

	constructor(private readonly authService: AuthService, private readonly messageService: MessageService, private readonly router: Router) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Add auth token to request
		const authReq = this.addAuthToken(req);

		return next.handle(authReq).pipe(
			timeout(APP_XHR_TIMEOUT),
			map((res) => this.handleSuccessfulResponse(res)),
			catchError((error: HttpErrorResponse) => this.handleErrorResponse(error, req, next)),
			finalize(() => this.handleRequestCompleted()),
		);
	}

	/**
	 * Add authorization token to request headers
	 */
	private addAuthToken(req: HttpRequest<any>): HttpRequest<any> {
		// Skip auth for non-API requests
		if (!this.isAuthenticationRequired(req.url)) {
			return req;
		}

		const token = this.authService.accessToken;
		if (token) {
			return req.clone({
				setHeaders: {
					Authorization: `Bearer ${token}`,
				},
			});
		}

		return req;
	}

	/**
	 * Check if authentication is required for this URL
	 */
	private isAuthenticationRequired(apiUrl: string): boolean {
		return apiUrl.includes(environment.baseURL) && !apiUrl.includes('/auth/login');
	}

	/**
	 * Handle successful HTTP responses
	 */
	private handleSuccessfulResponse(event: HttpEvent<any>): HttpEvent<any> {
		// Your API returns data directly without wrapping in 'response' property
		// So we return the event as-is
		return event;
	}

	/**
	 * Handle HTTP error responses
	 */
	private handleErrorResponse(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Handle 401 Unauthorized errors with refresh token logic
		if (error.status === 401 && this.isAuthenticationRequired(request.url)) {
			return this.handle401Error(request, next);
		}

		// Handle other HTTP errors
		this.handleOtherErrors(error);

		return throwError(() => error);
	}

	/**
	 * Handle 401 Unauthorized errors with token refresh
	 */
	private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Don't try to refresh if this is already a refresh request
		if (request.url.includes('/auth/refresh')) {
			this.authService.logout();
			this.router.navigate(['/sign-in']);
			return throwError(() => new Error('Refresh token expired'));
		}

		// If already refreshing, wait for the refresh to complete
		if (this.isRefreshing) {
			return this.refreshTokenSubject.pipe(
				filter((token) => token !== null),
				take(1),
				switchMap(() => next.handle(this.addAuthToken(request))),
			);
		}

		// Start the refresh process
		this.isRefreshing = true;
		this.refreshTokenSubject.next(null);

		return this.authService.refreshAccessToken().pipe(
			switchMap((newToken: string | null) => {
				if (newToken) {
					this.refreshTokenSubject.next(newToken);
					return next.handle(this.addAuthToken(request));
				} else {
					this.authService.logout();
					this.router.navigate(['/sign-in']);
					return throwError(() => new Error('Token refresh failed'));
				}
			}),
			catchError((err) => {
				this.authService.logout();
				this.router.navigate(['/sign-in']);
				return throwError(() => err);
			}),
			finalize(() => {
				this.isRefreshing = false;
			}),
		);
	}

	/**
	 * Handle non-401 HTTP errors
	 */
	private handleOtherErrors(error: HttpErrorResponse): void {
		let errorMessage: string;

		switch (error.status) {
			case 400:
				errorMessage = this.extractErrorMessage(error, 'Bad Request');
				break;
			case 403:
				errorMessage = 'Access forbidden';
				break;
			case 404:
				errorMessage = 'Resource not found';
				break;
			case 422:
				errorMessage = this.extractValidationErrorMessage(error);
				break;
			case 500:
				errorMessage = 'Internal server error';
				break;
			default:
				errorMessage = error.error?.message || error.message || 'Network error occurred';
		}

		// Show error notification for relevant errors
		if ([400, 403, 422, 500].includes(error.status)) {
			this.messageService.add({
				key: 'notification',
				severity: 'error',
				summary: 'Error',
				detail: errorMessage,
			});
		}

		console.error('HTTP Error:', error);
	}

	/**
	 * Extract error message from HTTP error response
	 */
	private extractErrorMessage(error: HttpErrorResponse, defaultMessage: string): string {
		if (error.error?.message) {
			if (Array.isArray(error.error.message)) {
				return error.error.message[0];
			}
			return error.error.message;
		}
		return defaultMessage;
	}

	/**
	 * Extract validation error message (422 errors)
	 */
	private extractValidationErrorMessage(error: HttpErrorResponse): string {
		const body = error.error;

		if (body?.fields && typeof body.fields === 'object') {
			const messages = Object.values(body.fields)
				.map((f: any) => f?.message)
				.filter(Boolean);
			if (messages.length > 0) return messages.join(', ');
		}

		if (body?.message && Array.isArray(body.message) && body.message.length > 0) {
			const errorObj = body.message[0];
			if (typeof errorObj === 'object') {
				const firstKey = Object.keys(errorObj)[0];
				return errorObj[firstKey];
			}
		}

		if (typeof body?.message === 'string') return body.message;

		return 'Validation error occurred';
	}

	/**
	 * Handle request completion
	 */
	private handleRequestCompleted(): void {
		// Cleanup or logging can be added here
	}
}
